import dayjs, { type Dayjs } from 'dayjs';
import type {
  CalendarPreviewResult,
  ContractManagementContractFileInDbExtended,
  ContractManagementDateInDb,
  ContractManagementExtractionInDb,
  ContractManagementViewColumnInDb,
} from '@/api';
import {
  DATE_TYPE_LABELS,
  parseCalendarEvent,
  type DateRecurrence,
} from '@/services/contractManagement/dateRow';

/** What narrows a calendar preview — the same for the month grid and the list. */
export interface CalendarPreviewFilters {
  text: string;
  onlyValueExtracted: boolean;
  onlyFuture: boolean;
  /** Contract type names to narrow to (`''` is untyped); empty means every type. */
  types: string[];
}

export function emptyCalendarPreviewFilters(): CalendarPreviewFilters {
  return { text: '', onlyValueExtracted: false, onlyFuture: false, types: [] };
}

/**
 * One dated entry a calendar preview shows: a fixed contract date, or one date
 * an extraction returned. Recurring entries are expanded per visible range by
 * `occurrencesInRange`.
 */
export interface CalendarPreviewEvent {
  key: string;
  /** `YYYY-MM-DD` of the first occurrence. */
  date: string;
  title: string;
  source: 'fixed' | 'extracted';
  file: ContractManagementContractFileInDbExtended;
  extraction: ContractManagementExtractionInDb | null;
  fixedDate: ContractManagementDateInDb | null;
  recurrence: DateRecurrence | null;
}

type PreviewRow = CalendarPreviewResult['results'][number];

const DAY = 'YYYY-MM-DD';

function toDay(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(DAY) : null;
}

/** Every dated entry one preview row carries — its fixed dates and its extraction. */
export function eventsFromPreviewRow([
  file,
  extraction,
  fixedDates,
]: PreviewRow): CalendarPreviewEvent[] {
  const events: CalendarPreviewEvent[] = [];

  for (const fixedDate of fixedDates) {
    const date = toDay(fixedDate.date);
    if (!date) continue;
    events.push({
      key: `fixed-${fixedDate.uid}`,
      date,
      title: DATE_TYPE_LABELS[fixedDate.type] ?? fixedDate.type,
      source: 'fixed',
      file,
      extraction: null,
      fixedDate,
      // The date carries its own `recurrence_*` keys.
      recurrence: fixedDate,
    });
  }

  if (!extraction) return events;
  const value = extraction.human_overwrite?.value ?? extraction.value;
  // A single date or event and a list of them read the same way.
  const items: unknown[] = Array.isArray(value) ? value : [value];

  for (const [index, item] of items.entries()) {
    const base = {
      key: `extracted-${extraction.uid}-${index}`,
      source: 'extracted' as const,
      file,
      extraction,
      fixedDate: null,
    };
    const event = parseCalendarEvent(item);
    if (event) {
      const date = toDay(event.event_date);
      if (!date) continue;
      events.push({ ...base, date, title: event.title ?? 'Extracted date', recurrence: event });
      continue;
    }
    const date = toDay(item);
    if (date) events.push({ ...base, date, title: 'Extracted date', recurrence: null });
  }

  return events;
}

const FREQUENCY_UNIT = {
  day: [1, 'day'],
  week: [1, 'week'],
  month: [1, 'month'],
  quarter: [3, 'month'],
  year: [1, 'year'],
} as const;

/**
 * The days (`YYYY-MM-DD`) on which `event` falls between `from` and `to`,
 * both inclusive. A non-recurring event yields at most its own date; an
 * infinite one is bounded by the range.
 */
export function occurrencesInRange(event: CalendarPreviewEvent, from: Dayjs, to: Dayjs): string[] {
  const start = dayjs(event.date);
  const recurrence = event.recurrence;
  const type = recurrence?.recurrence_type ?? 'never';
  const frequency = recurrence?.recurrence_frequency;

  // Extracted events are model output, so the frequency may be one we don't know.
  if (type === 'never' || !recurrence || !frequency || !Object.hasOwn(FREQUENCY_UNIT, frequency)) {
    return start.isBefore(from, 'day') || start.isAfter(to, 'day') ? [] : [event.date];
  }

  const [step, unit] = FREQUENCY_UNIT[frequency];
  const every = step * Math.max(1, recurrence.recurrence_interval ?? 1);
  const until = type === 'until_date' ? toDay(recurrence.recurrence_until_date) : null;
  const maxCount = type === 'count' ? (recurrence.recurrence_count ?? 1) : Infinity;

  const days: string[] = [];
  // Stepping from the start (not adding to the previous occurrence) keeps
  // month-end dates stable: Jan 31 + 1 month stays the 28th/29th, then the 31st.
  // Jump to just before the range, so a daily event from years back doesn't
  // walk every day in between.
  const skip = Math.max(0, Math.floor(from.diff(start, unit) / every) - 1);
  for (let n = skip; n < maxCount; n += 1) {
    const occurrence = start.add(n * every, unit);
    if (occurrence.isAfter(to, 'day')) break;
    if (until && occurrence.isAfter(until, 'day')) break;
    if (!occurrence.isBefore(from, 'day')) days.push(occurrence.format(DAY));
  }
  return days;
}

/**
 * The cell the extraction modal edits. The modal resolves a field's type from
 * a view column, and a calendar has none — the prompt lives on the extraction
 * itself — so the column is synthesised from the extraction.
 */
export function buildExtractionCell(
  extraction: ContractManagementExtractionInDb,
  fileUid: string
): {
  columnUid: string;
  extraction: ContractManagementExtractionInDb;
  column: ContractManagementViewColumnInDb;
  fileUid: string;
} {
  const column = {
    uid: extraction.uid,
    name: extraction.prompt,
    extraction_prompt: {
      prompt: extraction.prompt,
      response_type: extraction.response_type,
      example_data: extraction.example_data,
    },
  } as ContractManagementViewColumnInDb;
  return { columnUid: extraction.uid, extraction, column, fileUid };
}
